import {memo, useCallback} from 'react'
import {Settings} from 'lucide-react'
import {IconButton, Popover, PopoverTrigger, PopoverContent, Slider, Switch} from '@/shared/ui'
import type {NoiseGateProcessor} from './logic/noiseGateProcessor'
import type {InputSensitivityMode} from './logic/types'
import {MIN_THRESHOLD, MAX_THRESHOLD} from './logic/types'
import {useLocalAudioLevel} from './logic/useLocalAudioLevel'
import {AudioLevelMeter} from './AudioLevelMeter'

interface AudioSettingsPopoverProps {
  threshold: number
  mode: InputSensitivityMode
  processor?: NoiseGateProcessor | null
  level?: number
  autoThreshold?: number
  size?: 'small' | 'medium' | 'large'
  onThresholdChange: (value: number) => void
  onModeChange: (mode: InputSensitivityMode) => void
}

/**
 * Popover panel for audio input settings.
 * Contains a mode toggle (automatic/manual), a threshold slider,
 * and a real-time audio level meter.
 */
export const AudioSettingsPopover = memo(function AudioSettingsPopover({
  threshold,
  mode,
  processor = null,
  level: externalLevel,
  autoThreshold: externalAutoThreshold,
  size = 'small',
  onThresholdChange,
  onModeChange,
}: AudioSettingsPopoverProps) {
  const {level: processorLevel, autoThreshold: processorAutoThreshold} =
    useLocalAudioLevel(processor)
  const level = externalLevel ?? processorLevel
  const autoThreshold = externalAutoThreshold ?? processorAutoThreshold

  const isManual = mode === 'manual'

  const handleModeToggle = useCallback(
    (checked: boolean) => {
      onModeChange(checked ? 'manual' : 'automatic')
    },
    [onModeChange],
  )

  const handleSliderChange = useCallback(
    (values: number[]) => {
      if (values[0] !== undefined) {
        onThresholdChange(values[0])
      }
    },
    [onThresholdChange],
  )

  return (
    <Popover>
      <PopoverTrigger asChild>
        <IconButton
          variant="default"
          size={size}
          aria-label="Настройки звука"
          title="Настройки звука"
        >
          <Settings />
        </IconButton>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        sideOffset={12}
        className="w-72 bg-popover backdrop-blur-md border-border"
      >
        <div className="flex flex-col gap-4">
          <h3 className="text-sm font-medium text-popover-foreground">
            Чувствительность микрофона
          </h3>

          <div className="flex items-center justify-between">
            <label
              htmlFor="noise-gate-toggle"
              className="text-xs text-muted-foreground cursor-pointer select-none"
            >
              Ручной порог шумоподавления
            </label>
            <Switch
              id="noise-gate-toggle"
              size="sm"
              checked={isManual}
              onCheckedChange={handleModeToggle}
            />
          </div>

          <AudioLevelMeter
            level={level}
            threshold={threshold}
            autoThreshold={autoThreshold}
            isManual={isManual}
          />

          {isManual && (
            <Slider
              min={MIN_THRESHOLD}
              max={MAX_THRESHOLD}
              step={0.001}
              value={[threshold]}
              onValueChange={handleSliderChange}
              aria-label="Порог активации голоса"
            />
          )}

          <p className="text-[10px] text-muted-foreground leading-relaxed">
            {isManual
              ? 'Настройте ползунок так, чтобы зелёная полоска пересекала жёлтую линию только когда вы говорите.'
              : 'Порог автоматически подстраивается под фоновый шум.'}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  )
})
